import { useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import { focusFirstIn, focusWhenAble, rememberTrigger, type TriggerMemory } from './layerFocus';

export interface StackedLayer<K extends string> {
    key: K;
    content: ReactNode;
}

interface LayerProps {
    active: boolean;
    /** Home sits in the flow and sets the height; the others overlay it. */
    home: boolean;
    layerRef?: RefObject<HTMLDivElement>;
    onTrigger?: (target: EventTarget) => void;
    children: ReactNode;
}

function Layer({ active, home, layerRef, onTrigger, children }: LayerProps) {
    const ownRef = useRef<HTMLDivElement>(null);
    const ref = layerRef ?? ownRef;
    // React 18 has no `inert` prop, so it is set on the element.
    useLayoutEffect(() => {
        if (ref.current) ref.current.inert = !active;
    }, [active, ref]);
    return (
        <div
            ref={ref}
            aria-hidden={active ? undefined : true}
            onClickCapture={onTrigger && ((event) => onTrigger(event.target))}
            className={`flex min-w-0 flex-col ${home ? '' : 'absolute inset-0'} ${active ? '' : 'invisible'}`}
        >
            {children}
        </div>
    );
}

// The first layer is home: it alone sets the height, and the steps opened from it are laid over
// it, so switching never changes the height. Each step must fit within home's box.
export function StackedLayers<K extends string>({
    active,
    layers
}: {
    active: K;
    layers: readonly StackedLayer<K>[];
}) {
    const homeKey = layers[0]?.key;
    const stackRef = useRef<HTMLDivElement>(null);
    const homeRef = useRef<HTMLDivElement>(null);
    const trigger = useRef<TriggerMemory | null>(null);
    const previous = useRef(active);

    // Runs after the layers' own effects, so `inert` is already updated.
    useLayoutEffect(() => {
        if (previous.current === active) return;
        previous.current = active;
        const index = layers.findIndex((layer) => layer.key === active);
        const layerEl = stackRef.current?.children[index] as HTMLElement | undefined;
        if (!layerEl) return;
        if (active === homeKey) focusWhenAble(() => trigger.current, layerEl);
        else focusFirstIn(layerEl);
    }, [active, homeKey, layers]);

    return (
        <div ref={stackRef} className="relative flex min-w-0 flex-1 flex-col">
            {layers.map((layer) => (
                <Layer
                    key={layer.key}
                    active={layer.key === active}
                    home={layer.key === homeKey}
                    layerRef={layer.key === homeKey ? homeRef : undefined}
                    onTrigger={
                        layer.key === homeKey
                            ? (target) => {
                                  trigger.current = rememberTrigger(target, homeRef.current);
                              }
                            : undefined
                    }
                >
                    {layer.content}
                </Layer>
            ))}
        </div>
    );
}
